import { ContentError } from '@ctw/shared/components/errors/content-error';
import { DevelopmentErrorOverlay } from '@ctw/shared/components/errors/development-error-overlay';
import {
  TelemetryBoundary,
  type TelemetryContext,
  type TelemetryState,
  useTelemetry,
} from '@ctw/shared/context/telemetry/telemetry-boundary';
import { toError } from '@ctw/shared/utils/errors';
import { isError, pick } from 'lodash-es';
import {
  type ComponentType,
  createContext,
  type ReactNode,
  useContext,
  useMemo,
  useState,
} from 'react';
import { type FallbackProps, ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary';

// Read fresh on every call, not cached, so tests can stub NODE_ENV. The literal comparison is
// what lets Vite and webpack fold it to a constant and dead-code-eliminate the branches below.
const isLocalDevelopmentEnvironment = (): boolean => process.env.NODE_ENV === 'development';

interface OnErrorArgs {
  error: unknown;
  handled: boolean;
  bubbled: boolean;
  errorContext: TelemetryContext;
}
type OnErrorFn = (args: OnErrorArgs) => void;

interface HandleErrorArgs {
  error: unknown;
  bubbled: boolean;
  errorContext: TelemetryContext;
}
type HandleErrorFn = (args: HandleErrorArgs) => boolean;

interface ErrorMessageArgs {
  error: unknown;
}

type ErrorMessageFn = (args: ErrorMessageArgs) => string;

interface RenderErrorArgs {
  error: unknown;
  handled: boolean;
  bubbled: boolean;
  errorMessage: string;
  errorContext: TelemetryContext;
  resetErrorBoundary: FallbackProps['resetErrorBoundary'];
  componentStack: string | null;
}
type RenderErrorFn = (args: RenderErrorArgs) => ReactNode;

interface ErrorBoundaryState {
  ancestralBoundaryNames: Array<string>;
  boundaryName: string;
  errorContext: TelemetryContext;
}

interface InternalErrorBoundaryState extends ErrorBoundaryState {
  errorMessage: ErrorMessageFn;
  handleError: HandleErrorFn;
  renderError: RenderErrorFn;
  onError: OnErrorFn;
}

interface ErrorFlags {
  bubbled: boolean;
  handled: boolean;
  tracked: boolean;
}

interface FlaggedError extends Error, ErrorFlags {}

interface BubbledError extends FlaggedError {
  bubbled: true;
}

interface HandledError extends FlaggedError {
  handled: true;
}

interface TrackedError extends FlaggedError {
  tracked: true;
}

const isBubbledError = (error: unknown): error is BubbledError =>
  isError(error) && 'bubbled' in error && error.bubbled === true;

const isHandledError = (error: unknown): error is HandledError =>
  isError(error) && 'handled' in error && error.handled === true;

const isTrackedError = (error: unknown): error is TrackedError =>
  isError(error) && 'tracked' in error && error.tracked === true;

interface FlagErrorOptions extends ErrorFlags {}

const flagError = (
  error: Error | FlaggedError,
  { bubbled, handled, tracked }: FlagErrorOptions,
): FlaggedError => {
  (error as FlaggedError).bubbled = bubbled;
  (error as FlaggedError).handled = handled;
  (error as FlaggedError).tracked = tracked;
  return error as FlaggedError;
};

const ErrorBoundaryState = createContext<InternalErrorBoundaryState | null>(null);

const useInternalErrorBoundary = (): InternalErrorBoundaryState | null =>
  useContext(ErrorBoundaryState);

export const useErrorBoundary = (): ErrorBoundaryState | null => {
  const errorBoundaryState = useContext(ErrorBoundaryState);

  return errorBoundaryState
    ? pick(errorBoundaryState, ['ancestralBoundaryNames', 'boundaryName', 'errorContext'])
    : null;
};

interface BaseErrorBoundaryProps {
  boundaryName: string;
  initialContext?: TelemetryContext;
  errorMessage?: ErrorMessageFn;
  handleError?: HandleErrorFn;
  renderError?: RenderErrorFn;
  onError?: OnErrorFn;
}

interface ErrorBoundaryChildrenArgs {
  errorBoundary: ErrorBoundaryState;
  telemetry: TelemetryState;
}

export interface ErrorBoundaryProps extends BaseErrorBoundaryProps {
  children: (args: ErrorBoundaryChildrenArgs) => ReactNode;
}

export const ErrorBoundary = ({
  children,
  initialContext,
  boundaryName: localBoundaryName,
  errorMessage: localErrorMessage,
  handleError: localHandleError,
  renderError: localRenderError,
  onError: localOnError,
}: ErrorBoundaryProps) => {
  const telemetry = useTelemetry();
  const parentBoundary = useInternalErrorBoundary();
  const [latestComponentStack, setLatestComponentStack] = useState<string | null>(null);

  const localErrorContext = useMemo(
    () => ({
      ...(parentBoundary?.errorContext ?? {}),
      ...(telemetry.isScoped ? telemetry.getScopedContextProperties() : {}),
      ...initialContext,
      telemetryAncestralBoundaryNames: telemetry.ancestralBoundaryNames,
      telemetryBoundaryName: telemetry.boundaryName,
    }),
    [initialContext, parentBoundary?.errorContext, telemetry],
  );

  const errorBoundaryState: InternalErrorBoundaryState = useMemo(
    () => ({
      ancestralBoundaryNames: parentBoundary
        ? [...parentBoundary.ancestralBoundaryNames, parentBoundary.boundaryName]
        : [],
      boundaryName: localBoundaryName,
      errorContext: localErrorContext,
      errorMessage: ({ error }) => {
        if (localErrorMessage) {
          return localErrorMessage({ error });
        }

        if (parentBoundary?.errorMessage) {
          return parentBoundary.errorMessage({
            error,
          });
        }

        return 'Please try reloading the app.';
      },
      handleError: ({ error, bubbled, errorContext }) => {
        const handledByConsumer = localHandleError?.({ error, bubbled, errorContext }) ?? false;

        const handledByParentBoundary =
          parentBoundary?.handleError({
            error,
            bubbled,
            errorContext,
          }) ?? false;

        if (handledByConsumer || !handledByParentBoundary) {
          return true;
        }

        return false;
      },
      renderError: ({
        errorMessage,
        error,
        handled,
        bubbled,
        errorContext,
        resetErrorBoundary,
        componentStack,
      }) => {
        if (localRenderError) {
          return localRenderError({
            errorMessage,
            error,
            handled,
            bubbled,
            errorContext,
            resetErrorBoundary,
            componentStack,
          });
        }

        if (parentBoundary?.renderError) {
          return parentBoundary.renderError({
            errorMessage,
            error,
            handled,
            bubbled,
            errorContext,
            resetErrorBoundary,
            componentStack,
          });
        }

        if (isLocalDevelopmentEnvironment()) {
          return (
            <DevelopmentErrorOverlay
              error={toError(error)}
              componentStack={componentStack}
              boundaryName={localBoundaryName}
            />
          );
        }

        return <ContentError title={errorMessage} message="Please try reloading the app." />;
      },
      onError: ({ error, handled, bubbled, errorContext }) => {
        localOnError?.({ error, handled, bubbled, errorContext });

        parentBoundary?.onError({
          error,
          handled,
          bubbled,
          errorContext,
        });
      },
    }),
    [
      localBoundaryName,
      localErrorContext,
      localErrorMessage,
      localHandleError,
      localOnError,
      localRenderError,
      parentBoundary,
    ],
  );

  const forwardedErrorContext = useMemo(
    () => ({
      ancestralBoundaryNames: errorBoundaryState.ancestralBoundaryNames,
      boundaryName: localBoundaryName,
      context: localErrorContext,
    }),
    [errorBoundaryState.ancestralBoundaryNames, localBoundaryName, localErrorContext],
  );

  return (
    <TelemetryBoundary boundaryName={localBoundaryName} initialContext={initialContext}>
      {({ telemetry: localTelemetry }) => (
        <ErrorBoundaryState.Provider value={errorBoundaryState}>
          <ReactErrorBoundary
            onError={(thrownError, info) => {
              const error = toError(thrownError);
              setLatestComponentStack(info.componentStack ?? null);

              const previouslyHandled = isHandledError(error);
              const handled =
                previouslyHandled ||
                errorBoundaryState.handleError({
                  error,
                  bubbled: isBubbledError(error),
                  errorContext: forwardedErrorContext,
                });

              const previouslyTracked = isTrackedError(error);
              if (!previouslyTracked) {
                telemetry.trackError({
                  message: 'Unhandled error caught by error boundary',
                  error,
                  context: {
                    handled,
                    bubbled: isBubbledError(error),
                    errorContext: forwardedErrorContext,
                  },
                });

                // Make sure error boundary errors are always logged to the console, even when
                // telemetry is not available, even in production (note: telemetry.trackError
                // will not by default log to the console in production)
                // biome-ignore lint/suspicious/noConsole: intentional logging
                console.error(error, {
                  handled,
                  errorContext: forwardedErrorContext,
                });
              }

              flagError(error, {
                bubbled: isBubbledError(error),
                tracked: true,
                handled,
              });

              errorBoundaryState.onError({
                error,
                handled,
                bubbled: isBubbledError(error),
                errorContext: forwardedErrorContext,
              });

              // If we are not rendering the error, we should rethrow it so that it can be
              // caught and rendered by the parent boundary
              if (!(previouslyHandled || localRenderError)) {
                flagError(error, {
                  bubbled: true,
                  tracked: isTrackedError(error),
                  handled: isHandledError(error),
                });

                throw error;
              }
            }}
            fallbackRender={({ error, resetErrorBoundary }) => (
              <ReactErrorBoundaryFallbackRender
                error={error}
                errorMessage={localErrorMessage}
                renderError={errorBoundaryState.renderError}
                errorContext={forwardedErrorContext}
                resetErrorBoundary={resetErrorBoundary}
                componentStack={latestComponentStack}
                boundaryName={localBoundaryName}
              />
            )}
          >
            {children({
              errorBoundary: errorBoundaryState,
              telemetry: localTelemetry,
            })}
          </ReactErrorBoundary>
        </ErrorBoundaryState.Provider>
      )}
    </TelemetryBoundary>
  );
};

export interface WithErrorBoundaryProps<T> extends BaseErrorBoundaryProps {
  Component: ComponentType<T>;
}

export const withErrorBoundary =
  <TChildProps extends object>({
    Component,
    initialContext,
    boundaryName,
    errorMessage,
    renderError,
    handleError,
  }: WithErrorBoundaryProps<TChildProps>) =>
  (props: TChildProps) => {
    return (
      <ErrorBoundary
        boundaryName={boundaryName}
        initialContext={initialContext}
        errorMessage={errorMessage}
        handleError={handleError}
        renderError={renderError}
      >
        {() => <Component {...props} />}
      </ErrorBoundary>
    );
  };

interface ErrorBoundaryFallbackProps {
  error: unknown;
  errorMessage?: ErrorMessageFn;
  renderError?: RenderErrorFn;
  errorContext: TelemetryContext;
  resetErrorBoundary: FallbackProps['resetErrorBoundary'];
  componentStack: string | null;
  boundaryName: string;
}

const ReactErrorBoundaryFallbackRender = ({
  error: thrownError,
  errorMessage: errorMessageFn,
  renderError,
  errorContext,
  resetErrorBoundary,
  componentStack,
  boundaryName,
}: ErrorBoundaryFallbackProps) => {
  const parentBoundary = useInternalErrorBoundary();

  const error = toError(thrownError);
  const handled = isHandledError(error);
  const errorMessage = errorMessageFn?.({ error }) ?? 'An unexpected error occurred.';

  if (renderError) {
    return renderError({
      error,
      handled,
      bubbled: isBubbledError(error),
      errorMessage,
      errorContext,
      resetErrorBoundary,
      componentStack,
    });
  }

  if (parentBoundary) {
    flagError(error, {
      handled,
      bubbled: true,
      tracked: isTrackedError(error),
    });

    return parentBoundary.renderError({
      error,
      handled,
      bubbled: true,
      errorMessage,
      errorContext,
      resetErrorBoundary,
      componentStack,
    });
  }

  if (isLocalDevelopmentEnvironment()) {
    return (
      <DevelopmentErrorOverlay
        error={error}
        componentStack={componentStack}
        boundaryName={boundaryName}
      />
    );
  }

  return <ContentError title={errorMessage} message="Please try reloading the app." />;
};
